import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const ALL_FEATURES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'whatsapp',  label: 'WhatsApp & Bot' },
  { key: 'chats',     label: 'Live Chats' },
  { key: 'leads',     label: 'Leads CRM' },
];

function FeatureCheckboxes({ selected, onChange, availableFeatures }) {
  const available = availableFeatures || ALL_FEATURES.map(f => f.key);

  function toggle(key) {
    // dashboard, whatsapp, chats are always on (can't remove)
    if (['dashboard', 'whatsapp', 'chats'].includes(key)) return;
    if (selected.includes(key)) onChange(selected.filter(f => f !== key));
    else onChange([...selected, key]);
  }

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
      {ALL_FEATURES.filter(f => available.includes(f.key)).map(f => {
        const locked = ['dashboard', 'whatsapp', 'chats'].includes(f.key);
        const checked = selected.includes(f.key);
        return (
          <label key={f.key} style={{
            display: 'flex', alignItems: 'center', fontSize: 12, cursor: locked ? 'default' : 'pointer',
            background: checked ? 'rgba(56,189,248,0.1)' : 'rgba(2,5,16,0.7)', color: checked ? '#38bdf8' : '#64748b',
            padding: '4px 10px', borderRadius: 6, border: `1px solid ${checked ? 'rgba(56,189,248,0.3)' : 'rgba(99,102,241,0.15)'}`,
            opacity: locked ? 0.7 : 1,
          }}>
            <input type="checkbox" checked={checked} onChange={() => toggle(f.key)} style={{ marginRight: 5 }} disabled={locked} />
            {f.label}{locked ? ' (default)' : ''}
          </label>
        );
      })}
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'user', allowedFeatures: ['dashboard', 'whatsapp', 'chats'] });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editFeatures, setEditFeatures] = useState([]);

  const isSuperAdmin = user?.role === 'superadmin';
  const orgId = isSuperAdmin ? selectedOrgId : user?.organizationId;
  // Features the org allows — limits what we can grant users
  const orgFeatures = org?.allowedFeatures || user?.orgFeatures || ALL_FEATURES.map(f => f.key);

  // Superadmin: load org list
  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/api/org/organizations').then(({ data }) => {
        setOrgs(data);
        if (data.length > 0) setSelectedOrgId(data[0]._id);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Load org details for orgadmin
  useEffect(() => {
    if (!isSuperAdmin) {
      api.get('/api/org/my-org').then(({ data }) => setOrg(data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const fetchUsers = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get(`/api/org/users?organizationId=${orgId}`);
      setUsers(data);

      if (isSuperAdmin) {
        const found = orgs.find(o => o._id === orgId);
        if (found) setOrg(found);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [orgId, orgs, isSuperAdmin]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Keep create form's allowedFeatures in sync with org's allowed
  useEffect(() => {
    if (orgFeatures.length > 0) {
      setForm(f => ({ ...f, allowedFeatures: ['dashboard', 'whatsapp', 'chats'].filter(k => orgFeatures.includes(k)) }));
    }
  }, [orgFeatures.join(',')]); // eslint-disable-line

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await api.post('/api/org/users', { ...form, organizationId: orgId });
      setForm({ email: '', password: '', name: '', role: 'user', allowedFeatures: ['dashboard', 'whatsapp', 'chats'] });
      flash('User created! WhatsApp client is starting.');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    }
  }

  async function toggleActive(u) {
    try {
      await api.patch(`/api/org/users/${u._id}`, { isActive: !u.isActive });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, isActive: !u.isActive } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    }
  }

  async function saveFeatures(userId) {
    try {
      const { data } = await api.patch(`/api/org/users/${userId}`, { allowedFeatures: editFeatures });
      setUsers(prev => prev.map(x => x._id === userId ? data : x));
      setEditingUserId(null);
      flash('Access updated!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update features');
    }
  }

  const flash = msg => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 4000); };

  if (user?.role !== 'superadmin' && user?.role !== 'orgadmin') {
    return <div style={{ padding: 40, color: '#f1f5f9' }}>Access Denied. You must be an Org Admin.</div>;
  }

  const licenseUsed = users.length;
  const licenseTotal = org?.licenseCount ?? '?';
  const licensePercent = licenseTotal !== '?' ? Math.min(100, Math.round((licenseUsed / licenseTotal) * 100)) : 0;

  return (
    <div style={s.container}>
      <h2 style={s.title}>Users & Licenses</h2>
      <p style={s.desc}>Manage sub-users within your organization license quota. Control their access features.</p>

      {successMsg && <p style={{ color: '#22c55e', marginBottom: 16, fontWeight: 600 }}>{successMsg}</p>}
      {error && <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>}

      {/* Org Selector (superadmin only) */}
      {isSuperAdmin && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Select Organization</h3>
          <select style={s.input} value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)}>
            {orgs.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
          </select>
        </div>
      )}

      {/* License Meter */}
      {orgId && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>License Usage</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4 }}>
            <div style={{ flex: 1, background: 'rgba(2, 5, 16, 0.8)', borderRadius: 6, height: 8, overflow: 'hidden', border: '1px solid rgba(99,102,241,0.15)' }}>
              <div style={{
                height: '100%', width: `${licensePercent}%`, borderRadius: 6, transition: 'width 0.5s',
                background: licensePercent >= 100 ? 'linear-gradient(90deg,#ef4444,#f87171)' : licensePercent >= 80 ? 'linear-gradient(90deg,#fb923c,#fbbf24)' : 'linear-gradient(90deg,#6366f1,#818cf8)',
              }} />
            </div>
            <span style={{ color: licensePercent >= 100 ? '#ef4444' : '#94a3b8', fontWeight: 600, minWidth: 80 }}>
              {licenseUsed} / {licenseTotal} used
            </span>
          </div>
          {licensePercent >= 100 && (
            <p style={{ color: '#ef4444', marginTop: 8, fontSize: 13 }}>License limit reached. Contact your provider.</p>
          )}
        </div>
      )}

      {/* Create User */}
      {orgId && licensePercent < 100 && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Create New Sub-User</h3>
          <form onSubmit={handleCreate}>
            <div style={s.formRow}>
              <input type="text" placeholder="Full name" style={s.input} required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <input type="email" placeholder="Email" style={s.input} required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <input type="password" placeholder="Password" style={s.input} required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              <select style={s.input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="user">User</option>
                <option value="orgadmin">Org Admin</option>
              </select>
            </div>

            {form.role === 'user' && (
              <div style={{ marginBottom: 16 }}>
                <label style={s.featureLabel}>Access Features</label>
                <p style={{ color: '#64748b', fontSize: 12, margin: '4px 0 8px' }}>
                  Dashboard, WhatsApp & Chats are always included. Enable additional features below.
                </p>
                <FeatureCheckboxes
                  selected={form.allowedFeatures}
                  onChange={f => setForm({ ...form, allowedFeatures: f })}
                  availableFeatures={orgFeatures}
                />
              </div>
            )}

            <button type="submit" style={s.btn}>Create User</button>
          </form>
          <p style={{ color: '#64748b', fontSize: 12, marginTop: 12 }}>
            Each user gets their own WhatsApp number to scan independently.
          </p>
        </div>
      )}

      {/* Users Table */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Current Users ({users.length})</h3>
        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : users.length === 0 ? (
          <p style={{ color: '#64748b' }}>No users yet.</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>{['Name', 'Email', 'Role', 'Access Features', 'Status', 'Actions'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {users.map(u => (
                <React.Fragment key={u._id}>
                  <tr>
                    <td style={s.td}>{u.name}</td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: u.role === 'orgadmin' ? 'rgba(56,189,248,0.1)' : 'rgba(99,102,241,0.1)', color: u.role === 'orgadmin' ? '#38bdf8' : '#818cf8', border: `1px solid ${u.role === 'orgadmin' ? 'rgba(56,189,248,0.25)' : 'rgba(99,102,241,0.25)'}` }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={s.td}>
                      {u.role === 'orgadmin' ? (
                        <span style={{ color: '#64748b', fontSize: 12 }}>All org features</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(u.allowedFeatures || []).map(f => (
                            <span key={f} style={s.featurePill}>{FEATURE_LABELS[f] || f}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: u.isActive ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)', color: u.isActive ? '#34d399' : '#f87171', border: `1px solid ${u.isActive ? 'rgba(52,211,153,0.25)' : 'rgba(248,113,113,0.25)'}` }}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {u._id !== user?.id && (
                          <button
                            style={{ ...s.btnSm, background: u.isActive ? '#7f1d1d' : '#166534', color: u.isActive ? '#fca5a5' : '#86efac' }}
                            onClick={() => toggleActive(u)}
                          >
                            {u.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        {u.role === 'user' && (
                          <button
                            style={{ ...s.btnSm, background: 'rgba(56,189,248,0.1)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.2)' }}
                            onClick={() => { setEditingUserId(u._id); setEditFeatures(u.allowedFeatures || []); }}
                          >
                            Edit Access
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Inline feature editor row */}
                  {editingUserId === u._id && (
                    <tr>
                      <td colSpan={6} style={{ padding: '0 0 12px', background: '#0d1829' }}>
                        <div style={{ padding: '16px 20px', borderTop: '1px solid #1e3a5f' }}>
                          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>
                            Editing access for <strong style={{ color: '#f1f5f9' }}>{u.name}</strong>:
                          </p>
                          <FeatureCheckboxes
                            selected={editFeatures}
                            onChange={setEditFeatures}
                            availableFeatures={orgFeatures}
                          />
                          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                            <button style={s.btn} onClick={() => saveFeatures(u._id)}>Save Access</button>
                            <button style={{ ...s.btnSm, background: 'rgba(99,102,241,0.08)', color: '#64748b', border: '1px solid rgba(99,102,241,0.15)' }} onClick={() => setEditingUserId(null)}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const FEATURE_LABELS = { dashboard: 'Dashboard', whatsapp: 'WhatsApp', chats: 'Chats', leads: 'Leads' };

const s = {
  container: { padding: 40, flex: 1, background: 'transparent', color: '#e2e8f0', overflowY: 'auto' },
  title: { fontSize: 24, marginBottom: 8, fontWeight: 700, letterSpacing: -0.3 },
  desc: { color: '#64748b', marginBottom: 32, fontSize: 13 },
  card: { background: 'rgba(6, 12, 28, 0.9)', padding: 24, borderRadius: 12, border: '1px solid rgba(99,102,241,0.15)', marginBottom: 24, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' },
  cardTitle: { marginBottom: 16, color: '#e2e8f0', fontSize: 15, fontWeight: 600 },
  formRow: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  featureLabel: { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.18)', background: 'rgba(2, 5, 16, 0.85)', color: '#e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  btn: { padding: '9px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #6366f1, #818cf8)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit' },
  btnSm: { padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12, fontFamily: 'inherit' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontSize: 12, borderBottom: '1px solid rgba(99,102,241,0.1)', textTransform: 'uppercase', letterSpacing: 0.3 },
  td: { padding: '13px 12px', borderBottom: '1px solid rgba(99,102,241,0.06)', color: '#e2e8f0', fontSize: 13 },
  badge: { padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 },
  featurePill: { background: 'rgba(56,189,248,0.1)', color: '#38bdf8', padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, border: '1px solid rgba(56,189,248,0.2)' },
};
